import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * What the page is loading in the foreground, for the bar at the top of the screen.
 *
 * Deliberately opt-in rather than an axios interceptor: half the screens poll in the
 * background - the alerts, the health, the scanner - and a bar that lit up for every one of
 * those requests would always be on, and so say nothing. A screen registers what someone is
 * waiting for, with a word on what it is.
 */
interface BusyContextValue {
    /** What is loading now, oldest first. */
    labels: string[];
    start: (label: string) => number;
    stop: (id: number) => void;
}

const BusyContext = createContext<BusyContextValue>({
    labels: [],
    start: () => 0,
    stop: () => undefined,
});

export const BusyProvider = ({ children }: { children: ReactNode }) => {
    const [entries, setEntries] = useState<{ id: number; label: string }[]>([]);
    const next = useRef<number>(1);

    const start = useCallback((label: string) => {
        const id = next.current++;

        setEntries((current) => [...current, { id, label }]);

        return id;
    }, []);

    const stop = useCallback((id: number) => setEntries((current) => current.filter((e) => e.id !== id)), []);

    const value = useMemo(() => ({ labels: entries.map((e) => e.label), start, stop }), [entries, start, stop]);

    return <BusyContext.Provider value={value}>{children}</BusyContext.Provider>;
};

/**
 * Shows the bar at the top of the screen, with this label, for as long as `active` is true.
 */
export const useBusy = (active: boolean, label: string) => {
    const { start, stop } = useContext(BusyContext);

    useEffect(() => {
        if (!active) {
            return;
        }

        const id = start(label);

        return () => stop(id);
    }, [active, label, start, stop]);
};

/** What is loading in the foreground now; empty when nothing. */
export const useBusyLabels = () => useContext(BusyContext).labels;
